import { Modal, Typography } from "antd";
import { useState, type FC } from "react";
import { TargetPicker } from "../../../components";
import type { DeploymentTarget, Project } from "../../../types";
import type { AcfRow } from "../types";

const { Text } = Typography;

interface DeployModalProps {
  deployModal: { objectIds: string[] } | null;
  rows: AcfRow[];
  projects: Project[];
  targetsByObject: Record<string, DeploymentTarget[]>;
  deploying: boolean;
  onCancel: () => void;
  onConfirm: (environmentIds: string[]) => void;
}

// Mirrors snippets' deploy modal (same `kind`-based cross-project rule, see
// obsidian/Product/ACF Sync.md), kept as its own small copy rather than a generalization of the
// snippets component in place, to avoid touching the already-shipped, e2e-tested snippets UI.
export const DeployModal: FC<DeployModalProps> = ({ deployModal, rows, projects, targetsByObject, deploying, onCancel, onConfirm }) => {
  const [selectedEnvironmentIds, setSelectedEnvironmentIds] = useState<string[]>([]);

  const [lastDeployModal, setLastDeployModal] = useState(deployModal);
  if (deployModal !== lastDeployModal) {
    setLastDeployModal(deployModal);
    setSelectedEnvironmentIds([]);
  }

  const sourceProjectIds = new Set((deployModal?.objectIds ?? []).map((id) => rows.find((r) => r.id === id)?.projectId));
  const singleSourceProject = sourceProjectIds.size === 1 ? projects.find((p) => p.id === [...sourceProjectIds][0]) : undefined;
  const restrictedToOwnProject = singleSourceProject?.kind === "site";
  const pickerProjects = singleSourceProject && restrictedToOwnProject ? [singleSourceProject] : projects;

  const objectLabel = (id: string) => {
    const row = rows.find((r) => r.id === id);
    return row?.title || row?.key || id;
  };

  return (
    <Modal
      title={
        deployModal && deployModal.objectIds.length === 1
          ? `Deploy "${objectLabel(deployModal.objectIds[0])}" to`
          : `Deploy ${deployModal?.objectIds.length ?? 0} ACF objects to`
      }
      open={Boolean(deployModal)}
      onCancel={onCancel}
      onOk={() => onConfirm(selectedEnvironmentIds)}
      confirmLoading={deploying}
      okButtonProps={{ disabled: selectedEnvironmentIds.length === 0 }}
    >
      {deployModal && deployModal.objectIds.length > 1 && (
        <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
          {deployModal.objectIds.map((id) => objectLabel(id)).join(", ")}
        </Text>
      )}
      {restrictedToOwnProject && (
        <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
          This is a site project, it can only deploy to its own environments. Use a library
          project to share an ACF object across projects.
        </Text>
      )}
      <TargetPicker
        projects={pickerProjects}
        value={selectedEnvironmentIds}
        onChange={setSelectedEnvironmentIds}
        deployedTargets={deployModal && deployModal.objectIds.length === 1 ? targetsByObject[deployModal.objectIds[0]] : undefined}
      />
    </Modal>
  );
};
